import BookingInscription from '@/classes/Booking/BookingInscription.class';
import Formulaire_Empreinte_Carbone from '@/classes/Formulaire_Empreinte_Carbone/Formulaire_Empreinte_Carbone.class';
import Commande_Inscription from "@/classes/Commande/Commande_Inscription.class";
import { defineStore } from 'pinia';
import { useMainStore } from './mainStore';
import { useGroupeStore } from './groupeStore';

import UserService from '@/services/user.service';
import CartService from '@/services/booking/cart.service';
import PersistenceService from '@/services/booking/persistence.service';
import ValidationService from '@/services/booking/validation.service';
import TeamService from '@/services/booking/team.service';
import BibOfferService from '@/services/booking/bibOffer.service';
import CheckoutService from '@/services/booking/checkout.service';




export const useBookingStore = defineStore('booking', {
    state: () => ({
        id: null,
        uuid: null,
        id_module: null,
        id_account: 0,
        prix: 0,
        prix_total: 0,
        nb_articles: 0,
        taxe: 0,
        exclude_taxe: false,
        is_groupe: false,
        nom_groupe: "",
        inscriptions: [],
        transaction: null,
        initial_commande_inscription: null,
        formulaire_empreinte_carbone: null,
        paiement_admin: null,
        vip_ins: null,
        dossard_bourse: null,
        groupe: null,
    }),
    actions: {
        /**
         * Initialise le store de réservation avec les paramètres fournis
         * @param {number} id_module - ID du module
         * @param {boolean} exclude_taxe - Indique si la taxe doit être exclue
         * @param {number} id_account - ID du compte
         * @param {boolean} is_inscription_groupe - Indique si c'est une inscription de groupe
         */
        init(id_module, exclude_taxe, id_account, is_inscription_groupe) {
            this.id_module = id_module;
            this.exclude_taxe = exclude_taxe;
            this.id_account = id_account;
            this.is_groupe = is_inscription_groupe;
        },

        /**
         * Initializes inscriptions based on the given preselection.
         * If there are already inscriptions, the function returns early.
         * Adds a default inscription if none exist.
         * If a preselection is provided, initializes inscriptions based on the course type.
         * 
         * @param {Object} preselection - The preselection object containing course details.
         */
        init_inscriptions(preselection) {

            if (this.inscriptions.length > 0) {
                return;
            }
            // On ajoute une inscription par défaut
            this.addInscription();
            // Si une préselection est passée en paramètre, on initialise les inscriptions
            if (preselection && preselection.course) {
                // Si la course est une course en équipe ou en relais, on ajoute le nombre de coureurs nécessaires
                if ((preselection.course.type == "relais" || preselection.course.type == "equipe") && preselection.course.metadatas.nb_coureurs_equipe) {
                    let nb_coureurs_to_add = preselection.course.metadatas.nb_coureurs_equipe - 1;
                    for (let i = 0; i < nb_coureurs_to_add; i++) {
                        this.addInscription();
                    }
                }
                // Si la course est une course en duo, on ajoute un coureur
                if (preselection.course.type == "duo") {
                    this.addInscription();
                }
            }
            this.commitToLocalStorage();
        },

        handleCreatedTeams(equipes) {
            TeamService.handleCreatedTeams(equipes, this);
        },

        addMissingInscriptionToCompleteTeam(id_course, nb_inscriptions_with_id_course, nb_inscriptions_per_course) {
            TeamService.addMissingInscriptionToCompleteTeam(this, id_course, nb_inscriptions_with_id_course, nb_inscriptions_per_course);
        },

        deleteInscriptionsFromEquipe(equipe) {
            TeamService.deleteInscriptionsFromEquipe(this, equipe);
        },


        /**
         * Initialise une inscription depuis un dossard bourse
         * @param {Object} inscription - L'objet inscription à initialiser
         * @param {Object} dossard_bourse_data - Les données du dossard bourse (peut être dossard_bourse ou equipe_inscription)
         * @param {Object} mainStore - Le store principal contenant les données du module
         */
        _initializeInscriptionFromDossardBourse(inscription, dossard_bourse_data, mainStore) {
            inscription.lock_select_parcours = true;

            // Initialisation des courses
            inscription.list_courses.initialize_from_mainstore(mainStore.module.courses.list);
            inscription.toggle_course_selection_from_dossard_bourse(dossard_bourse_data);
            inscription.set_finished(false);

            // Initialisation des options
            inscription.list_options.initialize_from_mainstore(mainStore.module.options.list);
            inscription.filter_availables_options();
            inscription.list_options.toggle_from_dossard_bourse(dossard_bourse_data);

            // Initialisation des activités
            inscription.list_activites.initialize_from_mainstore(mainStore.module.activites.list);
            inscription.list_activites.toggle_from_dossard_bourse(dossard_bourse_data);

            // Initialisation des articles
            inscription.list_articles.initialize_from_mainstore(mainStore.module.articles_boutique.list);
            inscription.list_articles.toggle_from_dossard_bourse(dossard_bourse_data);
            inscription.list_articles.set_base_livraison_price(mainStore.module.frais_livraison);

            // Initialisation GIA et frais bourse
            inscription.initialize_gia(mainStore.module.has_gia);
            inscription.initialize_frais_bourse_dossard();

            // Initialisation des dons
            inscription.donation.initialize_from_mainstore(mainStore.module.donation);

            // Mise à jour des paniers
            inscription.update_all_carts(mainStore.inscription_vip, mainStore.bookingSystem?.groupe);
        },

        /**
         * Initialise les inscriptions depuis un dossard bourse
         * @param {Object} dossard_bourse - L'objet dossard bourse contenant les données d'initialisation
         */
        init_from_dossard_bourse(dossard_bourse) {
            if (this.inscriptions.length > 0) {
                return;
            }

            this.dossard_bourse = dossard_bourse;
            const mainStore = useMainStore();

            // Initialisation de la première inscription (dossard bourse principal)
            this.addInscription();
            this._initializeInscriptionFromDossardBourse(
                this.inscriptions[0],
                dossard_bourse,
                mainStore
            );

            // Initialisation des inscriptions d'équipe associées
            if (dossard_bourse.associated_equipe_inscriptions?.length > 0) {
                for (const equipe_inscription of dossard_bourse.associated_equipe_inscriptions) {
                    this.addInscription();
                    const index_new_inscription = this.inscriptions.length - 1;
                    this._initializeInscriptionFromDossardBourse(
                        this.inscriptions[index_new_inscription],
                        equipe_inscription,
                        mainStore
                    );
                }
            }

            // Calcul du panier global
            this.compute_cart();

            // Assignation des équipes si des inscriptions d'équipe existent
            if (dossard_bourse.associated_equipe_inscriptions?.length > 0) {
                mainStore.bookingSystem.equipes.assign_teams(false);
            }

            // Sauvegarde dans le localStorage
            this.commitToLocalStorage();
        },

        async show_payment_form() {
            const mainStore = useMainStore();
            const groupeStore = useGroupeStore();

            return await ValidationService.validateForPayment(this.$state, {
                isBookingDossardBourse: mainStore.dossard_bourse_mode && this.dossard_bourse,
                isBookingVip: mainStore.inscription_vip,
                isBookingAdmin: mainStore.inscription_as_admin,
                isBookingGroupeOpenCourse: mainStore.bookingSystem?.groupe?.inscription_course_complete,
                groupe: groupeStore.$state,
                dossard_bourse: this.dossard_bourse
            });

        },





        /**
         * Commits the booking data to session storage.
         * Note: Les données sont automatiquement supprimées à la fermeture de l'onglet.
         */
        commitToLocalStorage() {
            const mainStore = useMainStore();
            PersistenceService.save(this.$state, mainStore.module.base);
        },

        /**
         * Initializes the booking data from session storage if available and not expired.
         * 
         * This function checks for the presence of booking data and its expiration time in the session storage.
         * If the data is found and is not expired, it sets the booking data to the local state.
         * If the data is expired, it removes the booking data and its expiration time from the session storage.
         * Note: Les données sont automatiquement supprimées à la fermeture de l'onglet.
         */
        initializeFromLocalStorage() {
            const mainStore = useMainStore();
            PersistenceService.clearOtherModules(mainStore.module.base);
            const bookingData = PersistenceService.load(mainStore.module.base);
            if (bookingData) {
                this.setBookingFromLocalStorage(bookingData);
            }
        },

        clearState() {
            this.$reset();
        },

        clearLocalStorage() {
            const mainStore = useMainStore();
            PersistenceService.clear(mainStore.module.base);
        },

        /**
         * Clears session storage items that are not related to the current module.
         * 
         * This function iterates through all items in the session storage and removes
         * those that are related to booking but not associated with the current module.
         * 
         * The keys of the items to be removed must include either "sportips_booking_" 
         * or "sportips_booking_expires_", and must not include the base path of the 
         * current module.
         * 
         */
        clearLocalStorageFromOtherModules() {
            const mainStore = useMainStore();
            PersistenceService.clearOtherModules(mainStore.module.base);
        },

        /**
         * Sets the booking details from the provided session storage object.
         *
         */
        setBookingFromLocalStorage(local_booking) {
            this.id = local_booking.id ?? null;
            this.uuid = local_booking.uuid ?? null;
            this.id_module = local_booking.id_module ?? null;
            this.id_account = local_booking.id_account ?? 0;
            this.prix = local_booking.prix ?? 0;
            this.prix_total = local_booking.prix_total ?? 0;
            this.nb_articles = local_booking.nb_articles ?? 0;
            this.taxe = local_booking.taxe ?? 0;
            this.exclude_taxe = local_booking.exclude_taxe ?? false;
            this.is_groupe = local_booking.is_groupe ?? false;
            this.nom_groupe = local_booking.nom_groupe ?? "";
            this.setInscriptionsFromLocalStorage(local_booking.inscriptions ?? []);
            this.transaction = local_booking.transaction ?? null;
            this.paiement_admin = local_booking.paiement_admin ?? null;
            this.vip_ins = local_booking.vip_ins ?? null;
            this.dossard_bourse = local_booking.dossard_bourse ?? null;
            this.groupe = local_booking.groupe ?? null;
        },

        /**
         * Sets the inscriptions from session storage.
         * Iterates over the provided inscriptions array, creates a new Inscription
         * instance for each item, sets its data from session storage, and pushes it
         * to the inscriptions array of the current instance.
         *
         * @param {Array} inscriptions - The array of inscriptions data from session storage.
         */
        setInscriptionsFromLocalStorage(inscriptions) {
            const mainStore = useMainStore();
            const vip_ins = mainStore.inscription_vip;
            const lien_groupe = mainStore.bookingSystem?.groupe;

            for (let i = 0; i < inscriptions.length; i++) {

                let inscription = new BookingInscription(this.id_module);
                inscription.setInscriptionFromLocalStorage(inscriptions[i], vip_ins, lien_groupe);
                this.inscriptions.push(inscription);
            }
        },

        /**
         * Initializes the initial command inscription.
         * 
         * This asynchronous function creates a new instance of `Commande_Inscription`,
         * sets its ID, retrieves its data, computes any applicable reductions, and
         * assigns it to `this.initial_commande_inscription`.
         * 
         * @param {number} id_commande_inscription - The ID of the command inscription to initialize.
         * @returns {Promise<void>} A promise that resolves when the initialization is complete.
         */
        async init_initial_commande_inscription(id_commande_inscription) {
            const commande_inscription = new Commande_Inscription();
            commande_inscription.id = id_commande_inscription;
            await commande_inscription.get();
            commande_inscription.compute_reduction();
            this.initial_commande_inscription = commande_inscription;
        },

        /**
         * Adds a new inscription to the inscriptions array.
         * Creates a new Inscription instance with the current module ID and pushes it to the inscriptions array.
         */
        addInscription(id_course = null, lock_selection_parcours = false) {

            const mainStore = useMainStore();
            let ins = new BookingInscription(this.id_module);
            if (id_course) {
                ins.list_courses.initialize_from_mainstore(mainStore.module.courses.list)
                ins.add_course_from_preselection(id_course);
            }
            ins.lock_select_parcours = lock_selection_parcours;
            this.inscriptions.push(ins);
        },

        /**
         * Removes an inscription from the inscriptions array at the specified index.
         *
         * @param {number} index - The index of the inscription to remove.
         */
        removeInscription(index) {
            this.inscriptions.splice(index, 1);
        },

        /**
         * Checks the availabilities of a booking.
         *
         * @async
         * @returns {Promise<Object>} An object containing the status and message of the availability check.
         * @returns {boolean} return.status - The status of the availability check.
         * @returns {string[]} return.message - An array of messages, either errors or success messages.
         */
        async check_availabilities() {
            const groupeStore = useGroupeStore();
            return await ValidationService.checkAvailabilities(this.$state, groupeStore.$state);
        },

        async check_dossard_bourse_not_in_another_cart() {
            return await ValidationService.checkDossardBourseAvailability(this.dossard_bourse);

        },

        /**
         * Handles the process of posting a free checkout.
         * 
         * @returns {Promise<boolean>} A promise that resolves to true if both the post request and the paid request are successful, otherwise false.
         */
        async post_free_checkout() {
            return await CheckoutService.postFreeCheckout(this);
        },

        /**
         * Posts the admin checkout information.
         * 
         * @param {Object} paiement_admin - The payment information for the admin.
         * @returns {Promise<Object>} The response from the post_admin request.
         */
        async post_admin_checkout(paiement_admin) {
            return await CheckoutService.postAdminCheckout(this, paiement_admin);
        },

        /**
         * Posts the default checkout information.
         * 
         * This method retrieves necessary data from the main store, generates a UUID,
         * and sends a post request with the checkout information.
         * 
         * @async
         * @function post_default_checkout
         * @returns {Promise<Object>} The response from the post request.
         */
        async post_default_checkout() {
            return await CheckoutService.postDefaultCheckout(this);
        },

        /**
         * Handles the process of posting a free change of parcours checkout.
         * 
         * @returns {Promise<boolean>} A promise that resolves to a boolean indicating 
         * whether both the post request and the paid request were successful.
         */
        async post_free_change_parcours_checkout() {
            return await CheckoutService.postFreeChangeParcoursCheckout(this);
        },

        /**
         * Posts a transferable inscription.
         *
         * This method sends a transferable inscription to the server using the UserService.
         * It retrieves the main store, sets the VIP inscription and account ID, generates a UUID,
         * and then posts the transferable inscription.
         *
         * @param {Object} transferable_inscription - The inscription data to be transferred.
         * @returns {Promise<Object>} The response from the server.
         */
        async post_transfert_inscription(transferable_inscription) {
            return await CheckoutService.postTransfertInscription(this, transferable_inscription);
        },


        /**
         * Sets the paid free checkout status for the current booking.
         * 
         * This method calls the LyraService to set the paid free checkout status
         * using the current booking's UUID. If the operation is successful, it
         * updates the transaction information for the free checkout.
         * 
         * @returns {Promise<boolean>} A promise that resolves to a boolean indicating
         * whether the operation was successful.
         */
        async set_paid_free_checkout() {
            return await CheckoutService.setPaidFreeCheckout(this);
        },

        /**
         * Sets the paid/free checkout changement parcours.
         * 
         * This function calls the LyraService to set the paid/free checkout changement parcours
         * for the current booking identified by `this.uuid`. If the operation is successful,
         * it updates the transaction information for a free checkout.
         * 
         * @returns {Promise<boolean>} A promise that resolves to `true` if the operation was successful, otherwise `false`.
         */
        async set_paid_free_checkout_changement_parcours() {
            return await CheckoutService.setPaidFreeCheckoutChangementParcours(this);
        },

        /**
         * Checks if the current booking has already been paid.
         * 
         * This function calls the UserService to verify if the current booking
         * has already been paid. It returns a boolean indicating the payment status.
         * 
         * @returns {Promise<boolean>} A promise that resolves to true if the booking is paid, otherwise false.
         */
        async check_if_already_paid() {
            let res = await UserService.checkIfCommandeAlreadyPaid(this.$state);
            return res && res.paid;


        },

        /**
         * Sets the transaction information for a free checkout.
         * If the transaction object does not exist, it initializes it.
         * The transaction information includes an order ID and an amount of 0.
         */
        set_transaction_infos_free_checkout() {
            if (!this.transaction) this.transaction = {};
            this.transaction.commande_infos = { orderId: this.uuid, amount: 0 };
        },

        /**
         * Sets the transaction object.
         *
         * @param {Object} transaction - The transaction object to be set.
         */
        set_transaction(transaction) {
            this.transaction = transaction;
        },

        generate_uuid() {
            const UUID_CONFIG = {
                TIMESTAMP_SLICE: -2,
                RANDOM_MIN: 10,
                RANDOM_MAX: 90,
                PREFIX: "1"
            };
            if (!this.uuid) {
                const timestamp = Date.now().toString().slice(0, UUID_CONFIG.TIMESTAMP_SLICE);
                const firstrandomDigits = UUID_CONFIG.PREFIX + Math.floor(UUID_CONFIG.RANDOM_MIN + Math.random() * (UUID_CONFIG.RANDOM_MAX)).toString();
                const lastrandomDigits = Math.floor(UUID_CONFIG.RANDOM_MIN + Math.random() * (UUID_CONFIG.RANDOM_MAX)).toString();
                this.uuid = firstrandomDigits + timestamp + lastrandomDigits;
            }
        },


        get_nb_usage_code_reduction(id_code_reduction) {
            return this.inscriptions.filter(inscription => inscription.code_reduction && inscription.code_reduction.id == id_code_reduction).length;

        },


        async compute_cart() {
            this.generate_uuid();
            BibOfferService.handleDossardOffertGroupe(this);

            try {
                const cart = await CartService.computeCart(this.$state, {
                    includeTax: !this.exclude_taxe,
                    useCache: true,

                });
                this.prix = cart.prix;
                this.prix_total = cart.prix_total;
                this.nb_articles = cart.nb_articles;
                this.taxe = cart.taxe;
                CartService.invalidateCache(this.$state);
                return cart;
            }
            catch (error) {
                console.error("Error computing cart", error);
                return false;
            }
        },







        refresh_tarifs(refreshed_tarifs) {
            for (let i = 0; i < this.inscriptions.length; i++) {
                this.inscriptions[i].refresh_tarifs(refreshed_tarifs);
            }
        },

        /**
         * Met à jour les tarifs de toutes les inscriptions après modification de nb_reservations.
         * Recalcule le tarif sélectionné pour chaque inscription et change le tarif si les conditions ne sont plus remplies.
         */
        update_tarifs_after_nb_reservations_change() {
            const groupeStore = useGroupeStore();

            // Parcourir toutes les inscriptions
            for (let i = 0; i < this.inscriptions.length; i++) {
                const inscription = this.inscriptions[i];

                // Pour chaque inscription, mettre à jour le panier des courses
                // Cela va recalculer le tarif en vérifiant les places disponibles (nb_reservations)
                inscription.update_cart("courses", this.vip_booking, groupeStore.$state);

                // Recalculer le panier de l'inscription
                inscription.compute_cart();
            }

            // Recalculer le panier global
            this.compute_cart();

            // Sauvegarder dans le sessionStorage
            this.commitToLocalStorage();
        },

        /**
         * Validates the current booking state.
         * 
         * @async
         * @function get_validation
         * @returns {Promise<Object>} An object containing the validation status and message.
         * @returns {boolean} response.status - The validation status, true if valid, false otherwise.
         * @returns {string} response.message - The validation message providing details about the validation result.
         */
        async get_validation() {

            this.compute_cart();
            this.commitToLocalStorage();
            return await ValidationService.validateBooking(this.$state);

        },

        /**
         * Deletes the "DOSSARD OFFERT" code reduction from all inscriptions.
         */
        deleteBibOfferCode() {
            BibOfferService.deleteBibOfferCode(this);
        },

        /**
         * Initializes the carbon footprint form by creating a new instance of the Formulaire_Empreinte_Carbone class
         * and assigning it to the formulaire_empreinte_carbone property.
         * Used for VVX25
         */
        init_formulaire_empreinte_carbone() {
            this.formulaire_empreinte_carbone = new Formulaire_Empreinte_Carbone();
        },

        getLyraSimplifiedData() {
            let infos = {};
            infos.uuid = this.uuid;
            infos.id_account = this.id_account;
            infos.id_module = this.id_module;
            infos.prix = this.prix;
            infos.prix_total = this.prix_total;
            infos.nb_articles = this.nb_articles;
            infos.taxe = this.taxe;
            if (this.vip_ins) {
                infos.vip_ins = this.vip_ins;
            }
            if (this.paiement_admin) {
                infos.paiement_admin = this.paiement_admin;
            }
            infos.inscriptions = [];
            for (let i = 0; i < this.inscriptions.length; i++) {
                infos.inscriptions.push(this.inscriptions[i].getLyraSimplifiedData());
            }
            return infos;
        },

        getCheckAvailabilitiesSimplifiedData() {
            let data = {};
            data.id_module = this.id_module;
            data.inscriptions = [];
            for (let i = 0; i < this.inscriptions.length; i++) {
                data.inscriptions.push(this.inscriptions[i].getCheckAvailabilitiesData());
            }

            return data;
        }


    },
    getters: {

        has_at_least_one_preinscription_selected() {
            return this.inscriptions.some(inscription => inscription.is_preinscription());
        },

        has_inscriptions() {
            return this.inscriptions.length > 0;
        },
        has_initial_commande_inscription_loaded() {
            return this.has_inscriptions && this.initial_commande_inscription?.id_commande;
        },
        vip_booking() {
            const mainStore = useMainStore();
            if (!mainStore.inscription_vip) {
                return null;
            }
            return mainStore?.inscription_vip?.$state;
        },
        has_dossard_offert_groupe() {
            const mainStore = useMainStore();
            return mainStore.module.dossard_offert_groupe;
        },
        show_reglement_checkboxes() {
            if (!this.id_module) return false;

            if (this.inscriptions.length == 0) return false;

            let inscriptions_opened = this.inscriptions.filter((inscription) => inscription.finished == false);

            return this.nb_articles > 0 && this.prix >= 0 && inscriptions_opened.length == 0;
        },



        has_some_inscriptions_opened() {
            return this.inscriptions.filter((inscription) => inscription.finished == false).length > 0;
        },
        isInitialized() {
            return this.id_module && this.has_inscriptions;
        },

        has_remaining_credit() {
            if (!this.initial_commande_inscription) return false;
            if (this.initial_commande_inscription.prix && this.inscriptions[0].list_courses.cart) {
                if (this.inscriptions[0].list_courses.cart.nb_articles == 0) {
                    return false;
                }
                let credit = parseFloat(this.initial_commande_inscription.prix) - parseFloat(this.inscriptions[0].list_courses.cart.prix);
                credit = parseFloat(credit.toFixed(2));
                if (credit > 0) return credit;
            }
            return false;
        },




    },
});